import { Avatar, Grid2, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useNotify from '../../../../hooks/useNotify';
import BlockWithTitle from '../../../../components/atom/BlockWithTitle';
import { Add } from '@mui/icons-material';
import { isValidBooking } from '../../helpers/isValidBooking';
import { useAuth } from '../../../../hooks/useAuth';
import CustomOneFieldRenderer from './CustomOneFieldRenderer';
import TranslateIcon from '@mui/icons-material/Translate';
import { translateField } from '@openapi/translation/translation';
import { BOOKING_STATUSES, CUSTOM_FIELDS_TYPES } from '../../../../helpers/constants';
import stringAvatar from './helpers/stringAvatar';
import convertFieldNameDate from './helpers/convertFieldNameDate';

// Controlled by the page: `fields` is the lifted editable note list and
// `onFieldsChange` is its setter (accepts a value or updater, like setState).
// The page derives dirty/valid and persists via the unified Save.
function CustomFieldsDetails({ booking, readOnly, loading, fields, onFieldsChange }) {
    const { t, i18n } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const { user: currentAgent } = useAuth();
    const [translatingFields, setTranslatingFields] = useState(new Set());
    // Read-only viewers can't persist, so their translations live here instead of
    // in the page draft - otherwise translating a note to read it would dirty the
    // booking and trap them behind the unsaved-changes guard with no way to save.
    const [readOnlyTranslations, setReadOnlyTranslations] = useState({});

    // Processing bookings haven't completed checkout, so contact details can
    // still be empty - but agents should be able to edit additional
    // information regardless. Treat them as editable alongside valid bookings.
    const canEditAdditionalInformation =
        isValidBooking(booking) || booking?.status === BOOKING_STATUSES.PROCESSING;

    // Surface which required fields are still empty. They no longer block the
    // unified Save (the agent shouldn't be stuck on a field the guest skipped),
    // so this is the only cue that something the guest was asked for is missing.
    const emptyRequiredFields = fields.filter((field) => field.required && !field.value);
    const requiredNotice = emptyRequiredFields.length > 0 && (
        <Grid2 size={{ xs: 12 }}>
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'left',
                    color: 'warning.main',
                    fontStyle: 'italic',
                }}
            >
                {t('tickettypes.requiredFieldsEmpty', {
                    fields: emptyRequiredFields.map((field) => field.name).join(', '),
                })}
            </Typography>
        </Grid2>
    );

    const handleTranslateField = async (fieldIndex, fieldName, fieldValue) => {
        if (!fieldValue) return;

        const currentLanguage = i18n.language;
        const bookingLanguage = booking?.language || 'en';

        if (bookingLanguage === currentLanguage) {
            notifyError(t('common.translationNotNeeded'));
            return;
        }

        setTranslatingFields((prev) => new Set(prev).add(fieldIndex));

        try {
            const result = await translateField({
                originalLanguage: bookingLanguage,
                targetLanguage: currentLanguage,
                fieldTitle: fieldName,
                fieldValue: fieldValue,
            });

            const applied = {
                value: result.translatedValue,
                translatedTitle: result.translatedTitle,
                originalValue: fieldValue,
                originalTitle: fieldName,
            };

            if (readOnly) {
                setReadOnlyTranslations((prev) => ({ ...prev, [fieldIndex]: applied }));
            } else {
                onFieldsChange((prevFields) => {
                    const newFields = [...prevFields];
                    newFields[fieldIndex] = { ...newFields[fieldIndex], ...applied };
                    return newFields;
                });
            }

            notifySuccess(t('common.translated'));
        } catch (error) {
            notifyError(error?.message || t('common.translationError'));
        } finally {
            setTranslatingFields((prev) => {
                const newSet = new Set(prev);
                newSet.delete(fieldIndex);
                return newSet;
            });
        }
    };

    if (loading) {
        return null;
    }

    // Read-only viewers see the values but cannot edit them or add notes.
    if (readOnly) {
        return (
            <Grid2 size={{ xs: 12 }}>
                <BlockWithTitle title={t('tickettypes.additionalInformation')}>
                    {requiredNotice}
                    <Grid2 size={{ xs: 12 }}>
                        {fields.map((field, fieldIndex) => {
                            const currentLanguage = i18n.language;
                            const bookingLanguage = booking?.language || 'en';
                            const shouldShowTranslate =
                                bookingLanguage !== currentLanguage &&
                                field.value &&
                                field.value.trim();
                            const isTranslating = translatingFields.has(fieldIndex);
                            // Overlay any view-only translation for display; the
                            // translate button still works off the original value.
                            const displayField = readOnlyTranslations[fieldIndex]
                                ? { ...field, ...readOnlyTranslations[fieldIndex] }
                                : field;

                            return (
                                <Grid2 container sx={{ mb: 2 }} key={fieldIndex}>
                                    {field.type === CUSTOM_FIELDS_TYPES.CHAT ? (
                                        <>
                                            <Grid2 size={{ xs: shouldShowTranslate ? 10 : 12 }}>
                                                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                                                    <Grid2 sx={{ alignContent: 'center' }}>
                                                        <Tooltip
                                                            title={field.user?.name || 'Guest'}
                                                        >
                                                            <Avatar
                                                                {...stringAvatar(field.user?.name)}
                                                            />
                                                        </Tooltip>
                                                    </Grid2>
                                                    <Grid2 style={{ flex: 1 }}>
                                                        <TextField
                                                            fullWidth
                                                            required={field.required}
                                                            multiline
                                                            disabled={true}
                                                            label={
                                                                displayField.translatedTitle ||
                                                                convertFieldNameDate(field.name)
                                                            }
                                                            value={displayField.value || ''}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>
                                            {shouldShowTranslate && (
                                                <Grid2
                                                    size={{ xs: 2 }}
                                                    sx={{
                                                        alignContent: 'center',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Tooltip title={t('common.autoTranslate')}>
                                                        <IconButton
                                                            onClick={() =>
                                                                handleTranslateField(
                                                                    fieldIndex,
                                                                    field.name,
                                                                    field.value,
                                                                )
                                                            }
                                                            disabled={isTranslating}
                                                        >
                                                            <TranslateIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid2>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Grid2 size={{ xs: shouldShowTranslate ? 10 : 12 }}>
                                                <TextField
                                                    fullWidth
                                                    required={field.required}
                                                    multiline={
                                                        field.type === CUSTOM_FIELDS_TYPES.MULTI
                                                    }
                                                    disabled={true}
                                                    label={
                                                        displayField.translatedTitle || field.name
                                                    }
                                                    value={displayField.value || ''}
                                                />
                                            </Grid2>
                                            {shouldShowTranslate && (
                                                <Grid2
                                                    size={{ xs: 2 }}
                                                    sx={{
                                                        alignContent: 'center',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Tooltip title={t('common.autoTranslate')}>
                                                        <IconButton
                                                            onClick={() =>
                                                                handleTranslateField(
                                                                    fieldIndex,
                                                                    field.name,
                                                                    field.value,
                                                                )
                                                            }
                                                            disabled={isTranslating}
                                                        >
                                                            <TranslateIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid2>
                                            )}
                                        </>
                                    )}
                                </Grid2>
                            );
                        })}
                    </Grid2>
                </BlockWithTitle>
            </Grid2>
        );
    }

    // Editable, always exposed - changes are persisted by the page-level
    // unified Save (no per-block save button).
    return (
        <Grid2 size={{ xs: 12 }}>
            <BlockWithTitle title={t('tickettypes.additionalInformation')}>
                {requiredNotice}
                <Grid2 size={{ xs: 12 }}>
                    {fields.map((field, idx) => (
                        <Grid2 container key={idx} sx={{ mb: 2 }}>
                            <CustomOneFieldRenderer
                                field={field}
                                setField={(newValue) => {
                                    onFieldsChange((prevFields) => {
                                        const newFields = [...prevFields];
                                        newFields[idx] = {
                                            ...newFields[idx],
                                            value: newValue,
                                        };
                                        return newFields;
                                    });
                                }}
                                removeField={() => {
                                    onFieldsChange((prevFields) =>
                                        prevFields.filter((_, i) => i !== idx),
                                    );
                                }}
                            />
                        </Grid2>
                    ))}
                    {canEditAdditionalInformation && (
                        <Grid2 size={{ xs: 12 }} sx={{ mb: 2 }}>
                            <Tooltip title={t('tickettypes.addNewNote')}>
                                <span>
                                    <IconButton
                                        onClick={() => {
                                            onFieldsChange((prevFields) => [
                                                ...prevFields,
                                                {
                                                    name: `Note (${new Date().toISOString()})`,
                                                    description: '',
                                                    type: 'chat',
                                                    user: {
                                                        id: currentAgent.id,
                                                        name: currentAgent.name,
                                                    },
                                                    ticketField: false,
                                                    oldNote: false,
                                                    required: false,
                                                },
                                            ]);
                                        }}
                                    >
                                        <Add />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid2>
                    )}
                </Grid2>
            </BlockWithTitle>
        </Grid2>
    );
}

export default CustomFieldsDetails;
